<template>
  <!--
  DO NOT CHANGE THIS STRUCTURE
  v-app
    v-app-bar
    v-navigation-drawer
    v-main
    ...
   -->
  <v-app>
    <v-app-bar
      app
      clipped-left
      light
      color="white"
      height="45"
    >
      <v-toolbar-title>
        <AboutDialog><template #default>
        <v-img
          :alt="appName"
          class="shrink mr-2"
          contain
          :src="require(`../assets/logo_color.png`)"
          width="130"
          height="40"
        />
        </template></AboutDialog>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <TagsModal
        color="primary"
        :title="tagsTitle"
      ><template #default>
        <v-btn small text>
          <v-icon small class="mr-1">mdi-tag-multiple</v-icon>
          <span>{{ tagsTitle }}</span>
        </v-btn>
      </template></TagsModal>
      <TenantPanel :tenant="user.tenant" />
      <LanguagePanel />
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-btn text v-bind="attrs" v-on="on">
            <UserAvatar color="primary" :user="user" />
          </v-btn>
        </template>
        <v-list dense>
          <ProfileModal><template #default>
            <v-list-item-title>{{ profileTitle }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-account</v-icon>
            </v-list-item-icon>
          </template></ProfileModal>
          <ChangePasswordModal><template #default>
            <v-list-item-title>{{ changePasswordTitle }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-lock</v-icon>
            </v-list-item-icon>
          </template></ChangePasswordModal>
          <v-divider />
          <v-list-item class="red--text" @click.stop="logout">
            <v-list-item-title>{{ logoutTitle }}</v-list-item-title>
            <v-list-item-icon>
              <v-progress-circular
                v-if="logoutLoader"
                indeterminate
                color="red"
                size="22"
              />
              <v-icon v-else color="red">mdi-logout</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-navigation-drawer
      v-model="drawer"
      :mini-variant.sync="mini"
      app
      clipped
      permanent
      dark
      color="#0F172A"
    >
      <v-list class="pa-0">
        <v-list-item class="px-3 justify-end">
          <v-btn small icon @click.stop="mini = !mini">
            <v-icon v-text="mini ? `mdi-arrow-collapse-right`: `mdi-arrow-collapse-left`" small />
          </v-btn>
        </v-list-item>
      </v-list>
      <!-- Menus -->
      <v-list nav dense>
        <v-list-item-group color="white">
          <v-list-item
            link
            exact
            :to="{ name: 'dashboard' }"
          >
            <v-list-item-icon>
              <v-icon>mdi-view-dashboard</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ dashboardTitle }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="[-1,1].includes(Number(user.role))"
            link
            exact
            :to="{ name: 'users' }"
          >
            <v-list-item-icon>
              <v-icon>mdi-account-multiple</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ usersTitle }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            link
            exact
            :to="{ name: 'invoices' }"
          >
            <v-list-item-icon>
              <v-icon>mdi-receipt-text-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ invoicesTitle }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      <div v-if="!mini" class="built-with">
        <div class="built-with-content">
          <span>BUILT WITH</span>
          <v-img
            :alt="appName"
            contain
            :src="require(`../assets/procesio.png`)"
            width="80"
            height="25"
          />
        </div>
      </div>
    </v-navigation-drawer>
    <!-- DO NOT CHANGE THE CONTENT OF v-main ELEMENT -->
    <v-main class="default-layout">
      <v-alert
        dense
        dismissible
        class="flash-message"
        elevation="2"
        :type="flashAlert.type"
        v-model="flashAlert.show"
      >{{ flashAlert.message }}</v-alert>
      <slot />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue';

import { empty } from '@/utils/helpers';
import intl from '@/utils/intl';

import UserAvatar from '@/components/user-avatar.vue';
import LanguagePanel from '@/components/language-panel.vue';
import TenantPanel from '@/components/tenant-panel.vue';
import AboutDialog from '@/components/dialogs/about.vue';
import ProfileModal from '@/components/modals/profile.vue';
import ChangePasswordModal from '@/components/modals/change-password.vue';
import TagsModal from '@/components/modals/tags-modal.vue';

export default Vue.extend({
  props: {
    title: String
  },
  components: {
    UserAvatar,
    LanguagePanel,
    TenantPanel,
    AboutDialog,
    ProfileModal,
    ChangePasswordModal,
    TagsModal
  },
  data() {
    return {
      appName: process.env.VUE_APP_NAME,
      drawer: true,
      mini: false,
      user: this.$store.getters.user,
      logoutLoader: false,
      flashAlert: {
        timeID: -1,
        show: false,
        type: 'info',
        message: ''
      }
    }
  },
  computed: {
    // App menus
    dashboardTitle(): string {
      return intl.t('views.dashboard.title');
    },
    usersTitle(): string {
      return intl.t('views.users.title');
    },
    invoicesTitle(): string {
      return intl.t('views.invoices.title');
    },
    // User menu
    profileTitle(): string {
      return intl.t('views.profile.title');
    },
    tagsTitle(): string {
      return intl.t('views.tags.title');
    },
    changePasswordTitle(): string {
      return intl.t('views.changePassword.title');
    },
    logoutTitle(): string {
      return intl.t('general.logout');
    }
  },
  methods: {
    empty,
    async logout(): Promise<void> {
      this.logoutLoader = true;
      await this.$store.dispatch('logOut');
      await this.$router.push({
        name: 'login',
        query: { redirect: this.$route.path }
      });
    },
    setFlash(type: string, message: string): void {
      this.flashAlert.show = true;
      this.flashAlert.type = type;
      this.flashAlert.message = message;
      clearTimeout(this.flashAlert.timeID);
      this.flashAlert.timeID = setTimeout(() => this.flashAlert.show = false, 20000);
    }
  }
});
</script>

<style lang="scss" scoped>
.v-app-bar {
  user-select: none;
}

.default-layout {
  background-color: $layout-bg;
}

.flash-message {
  position: fixed !important;
  top: 25px;
  right: 10px;
  z-index: 100000;
  width: 290px;
}
</style>
